import {
  Box,
  Button,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  IconButton,
  Stack,
} from "@mui/material";
import React, { useEffect } from "react";
import { useUser } from "../contexts/useUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMAgents } from "../dataaccess/fetchLLMAgents";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { createLLMAgentAfterEdit } from "../dataaccess/createLLMAgentAfterEdit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { fetchScheduledPromptsForAgent } from "../dataaccess/fetchLLMScheduledPrompts";
import { updateLLMScheduledPromptStatus } from "../dataaccess/updateLLMScheduledPromptStatus";
import { 
  groupAgentsByBase, 
  parseAgentId, 
  formatVersionForDisplay, 
  getLatestVersion,
  getBaseAgentName 
} from "../utils/agentVersioning";

const Agents: React.FC = () => {
  // pick up agentId from route and tab from search
  const { project } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();

  // default tab from ?tab=scheduled or preview
  const tabParam = new URLSearchParams(location.search).get("tab");
  const defaultTab = tabParam === "scheduled" ? 1 : 0;
  const [selectedAgentTab, setSelectedAgentTab] = React.useState(defaultTab);
  const [selectedAgent, setSelectedAgent] = React.useState<any>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = React.useState<string>("ACTIVE");
  const [showMoreCount, setShowMoreCount] = React.useState<{ [key: string]: number }>({
    SUCCESS: 5,
    FAILED: 5
  });
  const [menuState, setMenuState] = React.useState<{
    anchorEl: HTMLElement | null;
    agent: any | null;
  }>({
    anchorEl: null,
    agent: null,
  });

  const open = Boolean(menuState.anchorEl);

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ["agents"] });
    setSelectedAgent(null);
  }, [project]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["agents"],
    queryFn: () => fetchLLMAgents(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  // Move agents declaration here, before useEffect
  const agents = data?.data?.agentsByProjectId?.items ?? [];

  // Scheduled prompts query
  const { 
    data: scheduledPrompts, 
    isLoading: isLoadingPrompts, 
    isError: isErrorPrompts 
  } = useQuery({
    queryKey: ['scheduledPrompts', selectedAgent?.id],
    queryFn: () => fetchScheduledPromptsForAgent(selectedAgent?.id),
    enabled: !!selectedAgent?.id && selectedAgentTab === 1,
  });

  // Filter and sort scheduled prompts
  const getFilteredAndSortedPrompts = () => {
    if (!scheduledPrompts?.data?.scheduledPromptsByAgentId?.items) return [];
    
    const allPrompts = scheduledPrompts.data.scheduledPromptsByAgentId.items;
    const filteredPrompts = allPrompts.filter((prompt: any) => 
      prompt.status?.toUpperCase() === selectedStatusFilter
    );

    if (selectedStatusFilter === "ACTIVE") {
      // Sort ACTIVE by nearest scheduledAt date
      return filteredPrompts.sort((a: any, b: any) => 
        new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
      );
    } else if (selectedStatusFilter === "SUCCESS" || selectedStatusFilter === "FAILED") {
      // Sort SUCCESS/FAILED by most recent processedAt, limit to showMoreCount
      const sorted = filteredPrompts.sort((a: any, b: any) => 
        new Date(b.processedAt || b.createdAt).getTime() - new Date(a.processedAt || a.createdAt).getTime()
      );
      return sorted.slice(0, showMoreCount[selectedStatusFilter] || 5);
    } else {
      // For DRAFT or other statuses, sort by creation date
      return filteredPrompts.sort((a: any, b: any) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }
  };

  const filteredPrompts = getFilteredAndSortedPrompts();
  const totalPromptsForStatus = scheduledPrompts?.data?.scheduledPromptsByAgentId?.items?.filter(
    (prompt: any) => prompt.status?.toUpperCase() === selectedStatusFilter
  ).length || 0;

  // Function to toggle prompt status between ACTIVE and DRAFT
  const togglePromptStatus = async (promptId: string, currentStatus: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    try {
      const newStatus = currentStatus?.toUpperCase() === "ACTIVE" ? "DRAFT" : "ACTIVE";
      await updateLLMScheduledPromptStatus(promptId, newStatus);     
      console.log(`Toggling prompt ${promptId} from ${currentStatus} to ${newStatus}`);
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['scheduledPrompts', selectedAgent?.id] });
    } catch (error) {
      console.error('Error toggling prompt status:', error);
    }
  };

  useEffect(() => {
    if (paramId && agents.length > 0) {
      const found = agents.find(a => a?.id === paramId);
      if (found) setSelectedAgent(found);
    }
  }, [paramId, agents]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("tab", selectedAgentTab === 1 ? "scheduled" : "preview");
    navigate({ search: params.toString() }, { replace: true });
  }, [selectedAgentTab]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    console.error("error", error);
    return <div>Error...</div>;
  }

  const getiFrameCode = (agent: any) => {
    return `<iframe
    src="${window.location.origin}/embedagent?id=${agent?.id}&customer_id=&customer_email="
    width="${agent?.displayWidth}"
    height="${agent?.displayHeight}"
    style="border: none;"
    allow="clipboard-read *; clipboard-write *"
  ></iframe>`;
  };

  const getReactCode = (agent: any) => {
    return `{/* setup:
1. npm i llmasaservice-ui 
2. add: import { AgentPanel } from "llmasaservice-ui";
3. if you haven't got a CSS file specified in the agent UI settings, you can use our default CSS file
import "../../node_modules/llmasaservice-ui/dist/index.css"; // check nesting level 
4. Add this code where you want the panel shown:  */}

<div> 
  <AgentPanel
    agent="${agent?.id}"
    customer={{
      // optional: omit the customer object if you don't want to use customer tracking features
      customer_id: "",
      customer_name: "",
      customer_user_id: "",
      customer_user_name: "",
  }}
  />
</div>`;
  };

  const getAgentStatusColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "draft":
        return "default";
      case "suspended":
      case "inactive":
        return "warning";
      default:
        return "default";
    }
  };

  const getAgentSize = (agent: any) => {
    if (!agent?.displayWidth && !agent?.displayHeight) return null;
    return `${agent?.displayWidth || "auto"} x ${agent?.displayHeight || "auto"}`;
  };

  const handleSelectedAgentTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedAgentTab(newValue);
  };





  // Status icon selector helper function
  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "success":
        return <CheckCircleIcon color="success" />;
      case "active":
        return <AccessTimeIcon color="primary" />;
      case "scheduled":
        return <PendingIcon color="info" />;
      case "draft":
        return <InfoIcon color="disabled" />;
      case "suspended":
        return <WarningIcon color="warning" />;
      case "fail1":
      case "fail2":
      case "fail3":
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="action" />;
    }
  };

  return (
    <>
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{ borderRadius: 1, border: "1px solid", borderColor: "divider" }}
            >
              <Box height="100%">
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6">Agents</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {groupAgentsByBase(agents).size} configured{" "}
                      {groupAgentsByBase(agents).size === 1 ? "agent" : "agents"}
                    </Typography>
                  </Box>
                  <Button onClick={() => navigate("new")} variant="contained" size="small">
                    New Agent
                  </Button>
                </Box>
                <List disablePadding>
                  {(() => {
                    // Group agents by base ID
                    const groupedAgents = groupAgentsByBase(agents);
                    const agentGroups = Array.from(groupedAgents.entries()).map(([baseId, versions]) => {
                      // Show the latest version by default, but include version info
                      const latestAgent = getLatestVersion(agents, baseId);
                      const hasMultipleVersions = versions.length > 1;
                      
                      return { baseId, versions, latestAgent, hasMultipleVersions };
                    });

                    return agentGroups.map(({ baseId, versions, latestAgent, hasMultipleVersions }) => {
                      if (!latestAgent) return null;
                      const isSelected =
                        selectedAgent?.id === latestAgent?.id ||
                        versions.some((version) => version.id === selectedAgent?.id);
                      const latestVersion = formatVersionForDisplay(
                        parseAgentId(latestAgent.id).version
                      );
                      const agentSize = getAgentSize(latestAgent);
                      
                      return (
                        <React.Fragment key={baseId}>
                          <ListItem
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "minmax(0, 1fr) auto",
                              },
                              gap: 1.5,
                              alignItems: "center",
                              cursor: "pointer",
                              backgroundColor: isSelected ? "action.selected" : "transparent",
                              '&:hover': {
                                backgroundColor: isSelected ? "action.selected" : "action.hover",
                              },
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              borderLeft: "3px solid",
                              borderLeftColor: isSelected ? "primary.main" : "transparent",
                              px: 2,
                              py: 1.5,
                            }}
                            onClick={(e) => {
                              // Only select agent if we didn't click a button
                              if (!(e.target as HTMLElement).closest("button")) {
                                setSelectedAgent(latestAgent);
                              }
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 0,
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getBaseAgentName(latestAgent?.name)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={latestAgent?.status || "unknown"}
                                  color={getAgentStatusColor(latestAgent?.status) as any}
                                  variant="outlined"
                                />
                                {hasMultipleVersions && (
                                  <Chip
                                    size="small"
                                    label={`${versions.length} versions`}
                                    variant="outlined"
                                    color="primary"
                                  />
                                )}
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mt: 0.5, color: "text.secondary" }}
                              >
                                {hasMultipleVersions && (
                                  <Typography variant="body2">Latest {latestVersion}</Typography>
                                )}
                                {agentSize && (
                                  <Typography variant="body2">Panel {agentSize}</Typography>
                                )}
                              </Stack>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: { xs: "flex-start", sm: "flex-end" },
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                variant="text"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuState({
                                    anchorEl: e.currentTarget,
                                    agent: latestAgent,
                                  });
                                }}
                              >
                                Embed
                              </Button>
                              <Menu
                                anchorEl={menuState.anchorEl}
                                open={open}
                                onClose={() =>
                                  setMenuState({ anchorEl: null, agent: null })
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "right",
                                }}
                              >
                                <MenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(
                                      getiFrameCode(menuState.agent)
                                    );
                                    setMenuState({ anchorEl: null, agent: null });
                                  }}
                                >
                                  iFrame Embed Code
                                </MenuItem>
                                <MenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(
                                      getReactCode(menuState.agent)
                                    );
                                    setMenuState({ anchorEl: null, agent: null });
                                  }}
                                >
                                  NPM Agent Panel (React, Next.js, etc)
                                </MenuItem>
                                <MenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(
                                      menuState.agent?.id
                                    );
                                    setMenuState({ anchorEl: null, agent: null });
                                  }}
                                >
                                  Copy Agent ID
                                </MenuItem>
                              </Menu>
                              <Button
                                variant="text"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`edit/${latestAgent?.id}`);
                                }}
                              >
                                Edit
                              </Button>
                            </Box>
                          </ListItem>
                          
                          {/* Show other versions if expanded and multiple versions exist */}
                          {hasMultipleVersions && selectedAgent?.id && 
                           versions.some(v => v.id === selectedAgent.id) && (
                            <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                              {versions
                                .filter(v => v.id !== latestAgent.id)
                                .map((version) => {
                                  const { version: versionNumber } = parseAgentId(version.id);
                                  return (
                                    <Box
                                      key={version.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        py: 0.75,
                                        pl: 3,
                                        pr: 2,
                                        backgroundColor: selectedAgent?.id === version.id ? "action.selected" : 'transparent',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: "action.hover",
                                        },
                                      }}
                                      onClick={() => setSelectedAgent(version)}
                                    >
                                      <Typography variant="body2" color="text.secondary">
                                        {formatVersionForDisplay(versionNumber)} · {version.name}
                                      </Typography>
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuState({
                                              anchorEl: e.currentTarget,
                                              agent: version,
                                            });
                                          }}
                                        >
                                          Embed
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`edit/${version.id}`);
                                          }}
                                        >
                                          Edit
                                        </Button>
                                      </Box>
                                    </Box>
                                  );
                                })}
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </List>
                {agents?.length === 0 && (
                  <Box p={2}>
                    <Typography variant="body2">
                      No agents found, would you like an example?
                    </Typography>
                    <Button
                      onClick={async () => {
                        await createLLMAgentAfterEdit({
                          projectId: project?.id ?? "",
                          name: "Example Travel Things to do Agent",
                          status: "active",
                          description:
                            "This example shows how to create a travel itinerary agent.",
                          systemInstructions: `You are a travel expert and the user is going to give you a city. Your main role is to suggest detailed itineraries for cities worldwide, considering popular attractions, local culture, dining options, and any special activities. Stick strictly to the topic of travel, and provide thoughtful, practical, and engaging recommendations. When users ask questions, keep your responses relevant to travel, focusing on itinerary suggestions, tips for exploring destinations, and advice on how to make the most of their trips.
                    
If you recommend a restaurant or dining choice, format it EXACTLY: 
                      
yelp[display name::uri encoded name:::uri encoded location] 
                    
where display name is the restaurant name, and uri encoded name, and uri encoded location are search strings safe for url search params.
                    
At the end of the response add a [Search for hotels in the city they entered](https://www.google.com/travel/search?q=the city they entered)  for example, if they entered Seattle as the location city, use https://www.google.com/travel/search?q=seattle.
                    
If you're asked any off-topic questions, politely reply that you're focused on travel, and suggest some travel related questions they might ask.`,
                          systemInstructionsAction: "append",
                          groupId: "",

                          displayTheme: "dark",
                          cssUrl: "",
                          displayTitle: "Things to do in city agent",
                          displayStartMessageOrPrompt: "message",
                          displayInitialMessageOrPrompt:
                            "I can suggest ideas and itineraries for the city you specify. Add other details that help me design the perfect plan. For example, I'm visiting Seattle for 2 days and love the outdoors and art.e",
                          displayPromptTemplate: "{{prompt}}",
                          displayFollowOnPrompts:
                            "3 days in Seattle, I like hiking & food | 5 night Maui trip young family",
                          displayActions: `[
 {
    "pattern": "[.\\\\s]*yelp\\\\[(.*)::(.*)::(.*)\\\\].*",
    "type": "markdown",
    "markdown": " [$1](https://www.yelp.com/search?find_desc=$2&find_loc=$3) "
  }
]`,
                          displayWidth: "100%",
                          displayHeight: "75vh",
                          data: "",
                          displayPlaceholder:
                            "What city and how long? Or type a follow-on question",
                          displayHideInitialPrompt: false,
                          displayShowSaveButton: true,
                          displayShowEmailButton: true,
                          displayShowCallToAction: true,
                          displayCallToActionButtonText: "Contact us",
                          displayCallToActionEmailAddress:
                            "support@llmasaservice.io",
                          displayCallToActionEmailSubject:
                            "TTD Example Agent CTA submitted",
                        });

                        queryClient.invalidateQueries({ queryKey: ["agents"] });
                      }}
                    >
                      Create Example Agent
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{ borderRadius: 1, border: "1px solid", borderColor: "divider" }}
            >
              <Box p={2}>
                {!selectedAgent ? (
                  <Typography variant="body2">
                    Select an agent to view details
                  </Typography>
                ) : (
                  <Tabs
                    value={selectedAgentTab}
                    onChange={handleSelectedAgentTabChange}
                  >
                    <Tab label="Preview" />
                    <Tab label="Scheduled Prompts" />
                  </Tabs>
                )}

                {selectedAgent && selectedAgentTab === 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "80vh",
                        p: 2,
                      }}
                    >
                      <iframe
                        src={`${window.location.protocol}//${window.location.host}/embedagent?id=${selectedAgent?.id}&customer_id=&customer_email=`}
                        width={selectedAgent?.displayWidth}
                        height={selectedAgent?.displayHeight}
                        style={{ flex: 1, border: "none" }}
                        allow="clipboard-read *; clipboard-write *"
                      ></iframe>
                    </Box>
                  </>
                )}

                {selectedAgent && selectedAgentTab === 1 && (
                  <Box sx={{ p: 2, height: "80vh", overflow: "auto" }}>
                    
                    {/* Status Filter Buttons */}
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {['ACTIVE', 'SUCCESS', 'FAILED', 'DRAFT', 'TEMPLATE'].map((status) => (
                        <Button
                          key={status}
                          variant={selectedStatusFilter === status ? "contained" : "outlined"}
                          size="small"
                          onClick={() => {
                            setSelectedStatusFilter(status);
                            // Reset show more count when changing filter
                            setShowMoreCount(prev => ({ ...prev, [status]: 5 }));
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          {status}
                        </Button>
                      ))}
                    </Box>

                    {isLoadingPrompts ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress />
                      </Box>
                    ) : isErrorPrompts ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography color="error">Error loading scheduled prompts</Typography>
                      </Box>
                    ) : filteredPrompts.length === 0 ? (
                      <Typography>No {selectedStatusFilter.toLowerCase()} scheduled prompts found for this agent</Typography>
                    ) : (
                      <>
                        <List>
                          {filteredPrompts.map((prompt: any) => (
                            <ListItem
                              key={prompt.id}
                              sx={{
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                mb: 1,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                },
                                cursor: 'pointer'
                              }}
                              onClick={() => navigate(`/scheduledprompts/edit/${prompt.id}`, {
                                state: { selectedAgent }
                              })}
                            >
                              <ListItemAvatar>
                                {getStatusIcon(prompt.status)}
                              </ListItemAvatar>
                              <ListItemText 
                                primary={prompt.title || prompt.promptText} 
                                secondary={
                                  <>
                                    <Typography component="span" variant="body2">
                                      Status: {prompt.status}
                                    </Typography>
                                    <br />
                                    {selectedStatusFilter === "ACTIVE" ? (
                                      <Typography component="span" variant="body2">
                                        Scheduled: {new Date(prompt.scheduledAt).toLocaleString()}
                                      </Typography>
                                    ) : (
                                      <>
                                        <Typography component="span" variant="body2">
                                          Scheduled: {new Date(prompt.scheduledAt).toLocaleString()}
                                        </Typography>
                                        {prompt.processedAt && (
                                          <>
                                            <br />
                                            <Typography component="span" variant="body2">
                                              Completed: {new Date(prompt.processedAt).toLocaleString()}
                                            </Typography>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </>
                                }
                              />
                              
                              {/* Pause/Resume Button for ACTIVE and DRAFT statuses */}
                              {(prompt.status?.toUpperCase() === "ACTIVE" || prompt.status?.toUpperCase() === "DRAFT") && (
                                <IconButton
                                  onClick={(e) => togglePromptStatus(prompt.id, prompt.status, e)}
                                  color={prompt.status?.toUpperCase() === "ACTIVE" ? "warning" : "primary"}
                                  title={prompt.status?.toUpperCase() === "ACTIVE" ? "Pause" : "Resume"}
                                  sx={{ ml: 1 }}
                                >
                                  {prompt.status?.toUpperCase() === "ACTIVE" ? <PauseIcon /> : <PlayArrowIcon />}
                                </IconButton>
                              )}
                            </ListItem>
                          ))}
                        </List>

                        {/* Show More Button for SUCCESS and FAILED */}
                        {(selectedStatusFilter === "SUCCESS" || selectedStatusFilter === "FAILED") && 
                         totalPromptsForStatus > (showMoreCount[selectedStatusFilter] || 5) && (
                          <Box display="flex" justifyContent="center" mt={2} mb={2}>
                            <Button
                              variant="outlined"
                              onClick={() => setShowMoreCount(prev => ({
                                ...prev,
                                [selectedStatusFilter]: (prev[selectedStatusFilter] || 5) + 5
                              }))}
                            >
                              Show More ({totalPromptsForStatus - (showMoreCount[selectedStatusFilter] || 5)} remaining)
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                    
                    <Box mt={2} display="flex" justifyContent="center" gap={2}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={() => navigate(`/scheduledprompts/new?agentId=${selectedAgent?.id}`, {
                          state: { selectedAgent }
                        })}
                      >
                        Create Scheduled Prompt
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => navigate(`/scheduledprompts/new?type=template&agentId=${selectedAgent?.id}`, {
                          state: { selectedAgent }
                        })}
                      >
                        Add Template
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Agents;
